import { useEffect, useState } from 'react'
import { catalogEndpoints, syncEndpoints } from '@/core/api/endpoints'
import { ApiError } from '@/core/api/fetchClient'
import { gravarCache, lerCache } from './carreteiroCache'

/**
 * Cabeçalho da ficha de carreteiro: chefe da obra e precheck das regras do
 * SIGA (medição gerada, transferência, contrato, retroativo) antes de lançar.
 */

// ============================================================================
//  Chefe da ficha — chefe do projeto da obra na data
// ============================================================================

export interface ChefeObraState {
  /**
   * live = confirmado online · cache = último conhecido (sem rede) ·
   * sem_chefe = a obra não tem chefe na data (bloqueia) ·
   * desconhecido = sem rede e sem cache (o servidor define no envio)
   */
  status: 'idle' | 'loading' | 'live' | 'cache' | 'sem_chefe' | 'desconhecido'
  chefe: { id: number; nome: string } | null
  message: string | null
}

interface ChefeCache {
  chefe: { id: number; nome: string }
}

export function useChefeObra(obraId: number | null | undefined, dt: string): ChefeObraState {
  const [state, setState] = useState<ChefeObraState>({ status: 'idle', chefe: null, message: null })

  useEffect(() => {
    if (obraId == null) {
      setState({ status: 'idle', chefe: null, message: null })
      return
    }
    let cancelled = false
    setState({ status: 'loading', chefe: null, message: null })

    ;(async () => {
      const key = `chefe:${obraId}`
      const previo = await lerCache<ChefeCache>(key)
      if (cancelled) return
      if (previo) setState({ status: 'cache', chefe: previo.chefe, message: null })
      try {
        const res = await catalogEndpoints.chefeObra(obraId, dt)
        if (cancelled) return
        if (res.chefe) {
          void gravarCache(key, { chefe: res.chefe } satisfies ChefeCache)
          setState({ status: 'live', chefe: res.chefe, message: null })
        } else {
          setState({ status: 'sem_chefe', chefe: null, message: res.message })
        }
      } catch {
        if (cancelled) return
        const cache = await lerCache<ChefeCache>(key)
        if (cancelled) return
        setState(
          cache
            ? {
                status: 'cache',
                chefe: cache.chefe,
                message: 'Sem rede — último chefe conhecido da obra. Será confirmado no envio.',
              }
            : {
                status: 'desconhecido',
                chefe: null,
                message: 'Sem rede — o chefe de equipe será definido pelo servidor no envio.',
              },
        )
      }
    })()

    return () => {
      cancelled = true
    }
  }, [obraId, dt])

  return state
}

// ============================================================================
//  Precheck — regras de cabeçalho do sync, sem gravar
// ============================================================================

export interface PrecheckState {
  status: 'idle' | 'loading' | 'ok' | 'bloqueado' | 'offline' | 'erro'
  bloqueios: Array<{ code: string; message: string }>
  avisos: string[]
  message: string | null
}

const PRECHECK_IDLE: PrecheckState = { status: 'idle', bloqueios: [], avisos: [], message: null }

export async function rodarPrecheck(obraId: number, mveId: number, dt: string): Promise<PrecheckState> {
  try {
    const r = await syncEndpoints.precheckCarreteiro(obraId, mveId, dt)
    return { status: r.ok ? 'ok' : 'bloqueado', bloqueios: r.bloqueios, avisos: r.avisos, message: null }
  } catch (e) {
    if (e instanceof ApiError && e.isOffline) {
      return { ...PRECHECK_IDLE, status: 'offline', message: 'Sem rede — as regras do SIGA serão conferidas no envio.' }
    }
    return {
      ...PRECHECK_IDLE,
      status: 'erro',
      message: `Não foi possível conferir as regras agora (${e instanceof Error ? e.message : String(e)}). Serão conferidas no envio.`,
    }
  }
}

export function usePrecheckCarreteiro(
  obraId: number | null | undefined,
  mveId: number | null | undefined,
  dt: string,
): PrecheckState {
  const [state, setState] = useState<PrecheckState>(PRECHECK_IDLE)

  useEffect(() => {
    if (obraId == null || mveId == null) {
      setState(PRECHECK_IDLE)
      return
    }
    let cancelled = false
    setState({ ...PRECHECK_IDLE, status: 'loading' })
    rodarPrecheck(obraId, mveId, dt).then((r) => {
      if (!cancelled) setState(r)
    })
    return () => {
      cancelled = true
    }
  }, [obraId, mveId, dt])

  return state
}
